/**
 * Scales, chord stacks and the arp sequence builder. Pure.
 * Transferred from Chirp/trill.html — SYRINX.SCALES / chordTones / triadQuality
 * and the module-level buildSeq. Simplified: the raga table is trimmed to the
 * sets this instrument actually offers in its scene selector.
 *
 * SINCE AV-CORE PHASE 3 (flyway intent 2026-10-04-av-core-inline):
 * chordTones, triadQuality and buildSeq ARE Chirp's single-source av-core
 * theory functions (../audio/vendor/av-core.mjs), called with THIS module's
 * SCALES table — the instrument's own subset (steps identical to av-core's;
 * tests/av-core-delegation.test.ts pins that) stays local, as av-core's README allows. Typed
 * wrappers only; edit the logic in Chirp av-core/theory.js.
 */

import {
  buildSeq as coreBuildSeq,
  chordTones as coreChordTones,
  triadQuality as coreTriadQuality,
} from '../audio/vendor/av-core.mjs';

export interface Scale {
  readonly label: string;
  readonly steps: readonly number[];
}

export const SCALES = {
  minor: { label: 'MINOR', steps: [0, 2, 3, 5, 7, 8, 10] },
  phrygian: { label: 'PHRYGIAN', steps: [0, 1, 3, 5, 7, 8, 10] },
  dorian: { label: 'DORIAN', steps: [0, 2, 3, 5, 7, 9, 10] },
  harmminor: { label: 'HARM MIN', steps: [0, 2, 3, 5, 7, 8, 11] },
  minpent: { label: 'MIN PENT', steps: [0, 3, 5, 7, 10] },
  majpent: { label: 'MAJ PENT', steps: [0, 2, 4, 7, 9] },
  blues: { label: 'BLUES', steps: [0, 3, 5, 6, 7, 10] },
  bhairavi: { label: 'BHAIRAVI', steps: [0, 1, 3, 5, 7, 8, 10] },
} as const satisfies Record<string, Scale>;

export type ScaleId = keyof typeof SCALES;
export const SCALE_IDS = Object.keys(SCALES) as ScaleId[];

export const QUALITIES = { triad: 3, seventh: 4, ninth: 5 } as const;
export type Quality = keyof typeof QUALITIES;

/**
 * Stacked diatonic thirds on a scale degree — semitone offsets from the scale
 * root, octave-wrapped. Every tone is in-scale by construction. (av-core)
 */
export function chordTones(scaleId: ScaleId, degree: number, quality: Quality): number[] {
  return coreChordTones(scaleId, degree, quality, SCALES);
}

/** Exact matches only — a raga stack earns 'open', not a mislabelled name. (av-core) */
export function triadQuality(scaleId: ScaleId, degree: number): 'maj' | 'min' | 'dim' | 'aug' | 'open' {
  return coreTriadQuality(scaleId, degree, SCALES);
}

export type ArpPattern = 'up' | 'down' | 'updown' | 'random';
export const ARP_PATTERNS: ArpPattern[] = ['up', 'down', 'updown', 'random'];

/**
 * The testable heart of the arp. 'up' repeats the chord figure per octave (the
 * classic arp move, not a global sort); 'updown' drops the doubled endpoints;
 * 'random' returns the note set and lets the step picker choose. (av-core)
 */
export function buildSeq(
  scaleId: ScaleId,
  degree: number,
  quality: Quality,
  octaves: number,
  pattern: ArpPattern,
  base: number,
): number[] {
  return coreBuildSeq(scaleId, degree, quality, octaves, pattern, base, SCALES);
}
